/**
 * Caption Types - Data model for Opus-style captions
 * These are animated visual overlays driven by transcript timing.
 * VTT is only used as timing reference - never burned directly.
 */

/**
 * @typedef {Object} CaptionWord
 * @property {string} text - The word text
 * @property {boolean} [emphasis] - Whether this word should be emphasized/highlighted
 * @property {number} [startTime] - Word-level start time (optional, for karaoke)
 * @property {number} [endTime] - Word-level end time (optional, for karaoke)
 */

/**
 * @typedef {Object} CaptionPosition
 * @property {number} x - X position (0-100, percentage)
 * @property {number} y - Y position (0-100, percentage)
 */

/**
 * @typedef {Object} CaptionBlock
 * @property {string} id - Unique identifier
 * @property {number} startTime - Start time in seconds
 * @property {number} endTime - End time in seconds
 * @property {CaptionWord[]} words - Array of words with optional emphasis
 * @property {string} preset - Preset ID ('karaoke', 'beasty', 'deepdiver', etc.)
 * @property {CaptionPosition} position - Position on screen
 */

/**
 * @typedef {Object} CaptionTrack
 * @property {string} clipId - Associated clip ID
 * @property {CaptionBlock[]} blocks - Array of caption blocks
 * @property {string} globalPreset - Default preset for new blocks
 */

// Preset IDs as constants
export const PRESET_IDS = {
    KARAOKE: 'karaoke',
    BEASTY: 'beasty',
    DEEP_DIVER: 'deepdiver',
    YOUSHAEI: 'youshaei',
    POD_P: 'podp',
    MOZI: 'mozi',
    POPLINE: 'popline',
    SIMPLE: 'simple',
    THINK_MEDIA: 'thinkmedia'
};

// Animation types
export const ANIMATION_TYPES = {
    WORD_HIGHLIGHT: 'word-highlight',    // Karaoke-style word by word
    SCALE_PUNCH: 'scale-punch',          // Pop/scale on current word
    FADE_SLIDE: 'fade-slide',            // Phrase fade in with slide
    OPACITY_REVEAL: 'opacity-reveal',    // Gradual opacity reveal
    POP_IN: 'pop-in',                    // Word pops in
    SLIDE_UP: 'slide-up',                // Line slides up
    FADE: 'fade',                        // Simple fade in/out
    KEYWORD_SWAP: 'keyword-swap',        // Highlight swaps between keywords
    NONE: 'none'                         // No animation
};

/**
 * Create a new CaptionBlock with defaults
 * @param {Partial<CaptionBlock>} partial 
 * @returns {CaptionBlock}
 */
export function createCaptionBlock(partial = {}) {
    return {
        id: partial.id || `caption_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        startTime: partial.startTime || 0,
        endTime: partial.endTime || 0,
        words: partial.words || [],
        preset: partial.preset || PRESET_IDS.KARAOKE,
        position: partial.position || { x: 50, y: 85 }
    };
}

/**
 * Create a CaptionTrack from VTT segments
 * @param {string} clipId 
 * @param {Array<{start: number, end: number, text: string}>} segments 
 * @param {string} preset 
 * @returns {CaptionTrack}
 */
export function createCaptionTrack(clipId, segments = [], preset = PRESET_IDS.KARAOKE) {
    return {
        clipId,
        globalPreset: preset,
        blocks: segments.map((seg, idx) => createCaptionBlock({
            id: `${clipId}_block_${idx}`,
            startTime: seg.start,
            endTime: seg.end,
            words: parseTextToWords(seg.text),
            preset
        }))
    };
}

/**
 * Parse text string into CaptionWord array
 * @param {string} text 
 * @returns {CaptionWord[]}
 */
export function parseTextToWords(text) {
    if (!text) return [];
    return text.split(/\s+/).filter(w => w.trim()).map(word => ({
        text: word,
        emphasis: false
    }));
}

/**
 * Calculate which word should be highlighted at a given time.
 * 
 * If words have real startTime/endTime (from Whisper word_timestamps=True),
 * uses those directly for precise karaoke timing.
 * Otherwise, falls back to even distribution across the block duration.
 * 
 * @param {CaptionBlock} block 
 * @param {number} currentTime 
 * @returns {number} Index of highlighted word, or -1 if outside block
 */
export function getHighlightedWordIndex(block, currentTime) {
    if (currentTime < block.startTime || currentTime > block.endTime) {
        return -1;
    }

    const wordCount = block.words.length;
    if (wordCount === 0) return -1;

    // Check if words have real timestamps (from Whisper or other precise source)
    const hasRealTimestamps = block.words[0] && 
        typeof block.words[0].startTime === 'number' && 
        typeof block.words[0].endTime === 'number' &&
        block.words[0].endTime > block.words[0].startTime;

    if (hasRealTimestamps) {
        // Use real word-level timestamps for precise highlighting
        for (let i = wordCount - 1; i >= 0; i--) {
            if (currentTime >= block.words[i].startTime) {
                return i;
            }
        }
        return 0;
    }

    // Fallback: even distribution (legacy VTT behavior)
    const duration = block.endTime - block.startTime;
    const elapsed = currentTime - block.startTime;
    const wordDuration = duration / wordCount;
    const wordIndex = Math.floor(elapsed / wordDuration);

    return Math.min(wordIndex, wordCount - 1);
}

/**
 * Check if a block is visible at the given time
 * @param {CaptionBlock} block 
 * @param {number} currentTime 
 * @returns {boolean}
 */
export function isBlockVisible(block, currentTime) {
    return currentTime >= block.startTime && currentTime <= block.endTime;
}
